class WristStrapDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleExternalOpen = this.handleExternalOpen.bind(this);
    this.handleExternalReset = this.handleExternalReset.bind(this);
    this.selectedOptions = {};
    this.previousScrollY = null;
    this.openedFromPdp = false;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-wrist-strap-overlay]');
    this.panel = this.querySelector('[data-wrist-strap-panel]');
    this.closeButtons = this.querySelectorAll('[data-wrist-strap-close]');
    this.addButton = this.querySelector('[data-wrist-strap-add]');
    this.cartAddButton = this.querySelector('[data-wrist-strap-cart-add]');
    this.priceDisplay = this.querySelector('[data-wrist-strap-price-display]');
    this.errorEl = this.querySelector('[data-wrist-strap-error]');
    this.variantData = this.parseVariantData();
    this.currency = this.dataset.currencySymbol || '£';
    this.discountEnabled = this.dataset.discountEnabled === 'true';
    this.discountPercent = parseFloat(this.dataset.discountPercent) || 0;
    this.discountNote = this.dataset.discountNote || '';

    this.optionInputs = Array.from(
      this.querySelectorAll('.wrist-strap-drawer__option-values input[type="radio"]'),
    );
    this.mediaGallery = this.querySelector('[data-wrist-strap-gallery]');
    this.galleryId = this.dataset.galleryId;
    this.optionLabels = Array.from(this.querySelectorAll('[data-option-label-position]'));
    this.ctaText = this.querySelector('[data-wrist-strap-cta-text]');
    this.cartContext = null;
    this.cartDrawer = document.querySelector('cart-drawer');

    this.defaultVariantId = Number(this.dataset.defaultVariantId);
    this.selectedVariant =
      this.variantData.find((variant) => variant.id === this.defaultVariantId) || this.variantData[0];

    if (this.selectedVariant) {
      this.selectedVariant.options.forEach((value, index) => {
        this.selectedOptions[index + 1] = value;
      });
    }

    this.attachEvents();
    this.updateOptionInputs();
    this.updateDisplays();
    this.updateGallery();
    this.updateCtaLabel();
  }

  disconnectedCallback() {
    document.removeEventListener('wrist-strap:open', this.handleExternalOpen);
    document.removeEventListener('wrist-strap:reset', this.handleExternalReset);
  }

  attachEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addButton?.addEventListener('click', () => this.addToProduct());
    this.cartAddButton?.addEventListener('click', () => this.addToProduct(true));
    this.optionInputs.forEach((input) =>
      input.addEventListener('change', (event) => this.handleOptionChange(event)),
    );

    document.addEventListener('wrist-strap:open', this.handleExternalOpen);
    document.addEventListener('wrist-strap:reset', this.handleExternalReset);
  }

  handleExternalReset(event) {
    const standaloneOnly = event?.detail?.standaloneOnly === true;
    const clearMirroredGiftWidget = event?.detail?.clearMirroredGiftWidget === true;
    const productFormId = event?.detail?.productFormId || '';
    const widgets = Array.from(document.querySelectorAll('[data-wrist-strap-widget]'));
    widgets.forEach((widget) => {
      if (standaloneOnly && widget.dataset.standalone !== 'true') return;
      if (productFormId && widget.dataset.productFormId !== productFormId) return;
      this.resetWidget(widget);
    });

    if (!clearMirroredGiftWidget) return;

    widgets.forEach((widget) => {
      if (widget.dataset.standalone === 'true') return;
      if (productFormId && widget.dataset.productFormId !== productFormId) return;

      const triggerBtn = widget.querySelector('[data-wrist-strap-trigger]');
      if (triggerBtn?.dataset.syncedFromStandalone !== 'true') return;

      this.resetWidget(widget);
    });
  }

  parseVariantData() {
    try {
      const jsonNode = this.querySelector('[data-wrist-strap-variants]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse wrist strap variant data', error);
      return [];
    }
  }

  handleExternalOpen(event) {
    const { button } = event.detail || {};
    if (button) {
      this.activeTrigger = button;
    } else {
      this.activeTrigger = document.querySelector('[data-wrist-strap-trigger]');
    }
    this.cartContext = event.detail?.cartContext || null;
    this.openedFromPdp = !this.cartContext && this.isPdpTrigger(this.activeTrigger);
    this.updateCtaLabel();
    this.closeCartDrawer();
    const requestedVariantId = Number(event.detail?.component?.variantId);
    const applied = this.applyVariantById(requestedVariantId);
    if (!applied) {
      this.syncSelectionFromWidget();
    }
    this.open();
  }

  syncSelectionFromWidget() {
    const widget = this.getActiveWidget();
    if (!widget) return;
    const selectedId = Number(widget.dataset.selectedVariantId || widget.dataset.defaultVariantId || this.dataset.defaultVariantId);
    this.applyVariantById(selectedId);
  }

  getActiveWidget() {
    const widgetFromTrigger = this.activeTrigger?.closest?.('[data-wrist-strap-widget]');
    if (widgetFromTrigger) return widgetFromTrigger;
    return document.querySelector('[data-wrist-strap-widget]');
  }

  applyVariantById(variantId) {
    if (!variantId || Number.isNaN(variantId)) return false;
    const variant = this.variantData.find((item) => item.id === variantId);
    if (!variant) return false;
    this.selectedVariant = variant;
    this.selectedOptions = {};
    variant.options.forEach((value, index) => {
      this.selectedOptions[index + 1] = value;
    });
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateGallery();
    return true;
  }

  open() {
    if (window.innerWidth <= 750) {
      this.previousScrollY = window.scrollY || 0;
    }
    document.body.classList.add('overflow-hidden');
    if (this.openedFromPdp) {
      document.body.classList.add('pdp-drawer-open');
    }
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  close() {
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('pdp-drawer-open');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    if (this.previousScrollY !== null) {
      window.scrollTo({ top: this.previousScrollY, behavior: 'auto' });
      this.previousScrollY = null;
    }
    if (this.errorEl) this.errorEl.textContent = '';
    this.cartContext = null;
    this.openedFromPdp = false;
    this.updateCtaLabel();
  }

  isPdpTrigger(trigger) {
    if (!trigger?.closest) return false;
    return Boolean(trigger.closest('[js-main-product]')) && !trigger.closest('.quick-add-modal');
  }

  updateSelectedVariant() {
    const match = this.variantData.find((variant) =>
      variant.options.every((value, index) => this.selectedOptions[index + 1] === value),
    );
    this.selectedVariant = match || null;
    this.updateDisplays();
    this.updateOptionInputs();
    this.updateGallery();
  }

  handleOptionChange(event) {
    const input = event.currentTarget;
    if (input.disabled) return;
    const optionPosition = this.getOptionPosition(input);
    this.selectedOptions[optionPosition] = input.value;
    this.ensureDependentOptionsAreAvailable(optionPosition);
    this.updateSelectedVariant();
  }

  ensureDependentOptionsAreAvailable(changedPosition) {
    const totalOptions = this.variantData?.[0]?.options?.length || 0;
    if (!totalOptions || changedPosition >= totalOptions) return;

    for (let optionPosition = changedPosition + 1; optionPosition <= totalOptions; optionPosition += 1) {
      const currentValue = this.selectedOptions[optionPosition];
      const hasValidCurrentValue =
        currentValue && this.isOptionValueAvailable(optionPosition, currentValue);

      if (hasValidCurrentValue) continue;

      const firstAvailableInput = this.optionInputs.find((input) => {
        const inputOptionPosition = this.getOptionPosition(input);
        if (inputOptionPosition !== optionPosition) return false;
        return this.isOptionValueAvailable(optionPosition, input.value);
      });

      if (firstAvailableInput) {
        this.selectedOptions[optionPosition] = firstAvailableInput.value;
      } else {
        delete this.selectedOptions[optionPosition];
      }
    }
  }

  getOptionPosition(input) {
    const optionAttr = input.getAttribute('data-option');
    if (optionAttr && optionAttr.includes('option')) {
      return Number(optionAttr.replace('option', ''));
    }
    const group = input.closest('[data-option-position]');
    return Number(group?.dataset.optionPosition);
  }

  updateOptionInputs() {
    if (!this.optionInputs?.length) return;
    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const shouldCheck = this.selectedOptions[optionPosition] === input.value;
      input.checked = shouldCheck;
      input.nextElementSibling?.classList.toggle('is-active', shouldCheck);
    });
    this.updateOptionLabels();
    this.updateOptionAvailability();
  }

  updateGallery() {
    if (!this.mediaGallery) return;

    const colorValue = this.getSelectedColor();
    const galleryKey = this.galleryId;
    const featuredMediaId = this.selectedVariant?.featured_media?.id;
    const preferredKey = galleryKey && featuredMediaId ? `${galleryKey}-${featuredMediaId}` : null;
    const selectedImageSrc = this.getSelectedVariantImageSrc();

    const slides = Array.from(this.mediaGallery.querySelectorAll('.product__media-item'));
    if (!slides.length) return;

    const normalizeImageSrc = (value) => {
      if (!value) return '';
      return value
        .replace(/^https?:/, '')
        .split('?')[0]
        .toLowerCase();
    };

    const getMediaAlt = (element) => {
      if (!element) return '';
      return (
        element.dataset.mediaAlt ||
        element.getAttribute('data-media-alt') ||
        element.querySelector('img')?.getAttribute('alt') ||
        ''
      ).toLowerCase();
    };

    const matchesColor = (element) => {
      if (!colorValue) return false;
      const mediaAlt = getMediaAlt(element);
      const normalizedAlt = mediaAlt.replace(/[^a-z0-9]/g, '');
      return normalizedAlt.includes(colorValue);
    };

    const matchesSelectedImage = (element) => {
      if (!selectedImageSrc) return false;
      const slideImage = element.querySelector('img');
      const slideSrc = normalizeImageSrc(slideImage?.currentSrc || slideImage?.src || '');
      const targetSrc = normalizeImageSrc(selectedImageSrc);
      return Boolean(slideSrc && targetSrc && slideSrc === targetSrc);
    };

    const colorSlides = colorValue ? slides.filter((slide) => matchesColor(slide)) : [];
    const defaultSlides = slides.filter((slide) => {
      const alt = slide.dataset.mediaAlt?.toLowerCase();
      return !alt || (!alt.includes('color_') && !alt.includes('alphabet_'));
    });
    const visibleSlides =
      colorSlides.length > 0 ? colorSlides : defaultSlides.length > 0 ? defaultSlides : slides;

    slides.forEach((slide) => {
      const shouldShow = visibleSlides.includes(slide);
      slide.classList.toggle('is-hidden', !shouldShow);
      slide.classList.toggle('product__media-item--variant', !shouldShow);
    });

    const targetSlide =
      visibleSlides.find((slide) => preferredKey && slide.dataset.mediaId === preferredKey) ||
      visibleSlides.find(
        (slide) =>
          featuredMediaId && slide.dataset.mediaId?.endsWith(`-${featuredMediaId}`),
      ) ||
      visibleSlides.find((slide) => matchesSelectedImage(slide)) ||
      visibleSlides[0];

    if (!targetSlide) return;

    slides.forEach((slide) => slide.classList.remove('is-active'));
    targetSlide.classList.add('is-active');

    const sliderComponent = this.mediaGallery.querySelector('slider-component');
    sliderComponent?.resetPages?.();

    const sliderTrack = sliderComponent?.querySelector('[id^="Slider-"]');
    if (sliderTrack) {
      sliderTrack.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth',
      });
    }

    const visibleTargets = new Set(visibleSlides.map((slide) => slide.dataset.mediaId));
    const thumbnailItems = Array.from(this.mediaGallery.querySelectorAll('.thumbnail-list__item'));
    thumbnailItems.forEach((item) => {
      const target = item.dataset.target;
      const button = item.querySelector('button');
      let shouldShow = visibleTargets.has(target);

      if (!shouldShow && colorValue) {
        const altAttr = getMediaAlt(item);
        const normalizedAlt = altAttr.replace(/[^a-z0-9]/g, '');
        if (normalizedAlt.includes(colorValue)) {
          shouldShow = true;
        }
      }

      item.classList.toggle('is-hidden', !shouldShow);
      item.classList.toggle('thumbnail-list_item--variant', !shouldShow);
      if (shouldShow && target === targetSlide.dataset.mediaId) {
        button?.setAttribute('aria-current', 'true');
      } else {
        button?.removeAttribute('aria-current');
      }
    });
  }

  getSelectedColor() {
    const activeInput = this.optionInputs.find(
      (input) => input.checked && input.name?.toLowerCase().includes('color'),
    );
    if (activeInput?.value) {
      return this.normalizeColorValue(activeInput.value);
    }

    const option1 = this.selectedVariant?.option1;
    if (option1) {
      return this.normalizeColorValue(option1);
    }

    return null;
  }

  getDisplayColor() {
    const activeInput = this.optionInputs.find(
      (input) => input.checked && input.name?.toLowerCase().includes('color'),
    );
    if (activeInput?.value) {
      return activeInput.value;
    }

    return this.selectedVariant?.option1 || this.selectedVariant?.options?.[0] || null;
  }

  normalizeColorValue(value) {
    if (!value) return null;
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  updateOptionLabels() {
    if (!this.optionLabels?.length) return;
    this.optionLabels.forEach((label) => {
      const position = Number(label.dataset.optionLabelPosition);
      const value = this.selectedOptions[position];
      if (value) {
        label.textContent = value;
      }
    });
  }

  updateOptionAvailability() {
    if (!this.optionInputs?.length || !this.variantData?.length) return;

    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const isAvailable = this.isOptionValueAvailable(optionPosition, input.value);
      const label = input.nextElementSibling;

      input.disabled = !isAvailable;
      input.classList.toggle('disabled', !isAvailable);
      label?.classList.toggle('out-of-stock', !isAvailable);
    });
  }

  isOptionValueAvailable(optionPosition, optionValue) {
    return this.variantData.some((variant) => {
      if (!variant?.available) return false;
      if (variant.options?.[optionPosition - 1] !== optionValue) return false;

      for (let posNumber = 1; posNumber < optionPosition; posNumber += 1) {
        const selectedValue = this.selectedOptions[posNumber];
        if (!selectedValue) continue;
        if (variant.options?.[posNumber - 1] !== selectedValue) return false;
      }

      return true;
    });
  }

  updateDisplays() {
    if (!this.selectedVariant) {
      if (this.priceDisplay) this.priceDisplay.textContent = 'Unavailable';
      if (this.addButton) this.addButton.disabled = true;
      if (this.cartAddButton) this.cartAddButton.disabled = true;
      return;
    }

    const variantPrice = this.getEffectivePrice(this.selectedVariant.price);
    const priceHtml = this.getPriceHtml(variantPrice, this.selectedVariant.price);
    if (this.priceDisplay) {
      this.priceDisplay.innerHTML = priceHtml;
    }
    if (this.addButton) {
      this.addButton.disabled = !this.selectedVariant.available;
    }
    if (this.cartAddButton) {
      this.cartAddButton.disabled = Boolean(this.cartContext) || !this.selectedVariant.available;
    }
  }

  getEffectivePrice(priceInCents) {
    if (!this.discountEnabled) return priceInCents;
    const multiplier = Math.max(0, 100 - this.discountPercent) / 100;
    const discounted = Math.round(priceInCents * multiplier);
    return Math.max(0, discounted);
  }

  formatCurrency(cents) {
    const amount = cents / 100;
    const formatted = amount.toFixed(2);
    return `${this.currency}${formatted}`;
  }

  getPriceHtml(finalPrice, originalPrice, { wrapOriginal = false } = {}) {
    if (finalPrice <= 0) {
      const freeLabel = this.discountNote || 'FREE';
      const label = wrapOriginal ? `(${freeLabel})` : freeLabel;
      const compare = originalPrice > 0 ? ` <s>${this.formatCurrency(originalPrice)}</s>` : '';
      return `${label}${compare}`;
    }

    if (originalPrice > finalPrice) {
      return `${this.formatCurrency(finalPrice)} <s>${this.formatCurrency(originalPrice)}</s>`;
    }

    const original = this.formatCurrency(originalPrice);
    return wrapOriginal ? `(${original})` : original;
  }

  addToProduct(submitProductForm = false) {
    if (!this.selectedVariant) {
      this.setErrorMessage('Please select a valid option.');
      return;
    }

    if (!this.selectedVariant.available) {
      this.setErrorMessage('Selected option is unavailable.');
      return;
    }

    this.setErrorMessage('');
    const finalPrice = this.getEffectivePrice(this.selectedVariant.price);
    if (this.cartContext) {
      this.addStrapToCart(finalPrice);
    } else {
      const productForm = submitProductForm ? this.getAssociatedProductForm() : null;
      if (submitProductForm && !productForm) {
        this.setErrorMessage('Unable to find the product form.');
        return;
      }
      this.updateGiftWidget(finalPrice);
      if (productForm) {
        this.submitProductForm(productForm);
        this.clearDirectCartActiveState();
      } else {
        this.close();
      }
    }
  }

  getAssociatedProductForm() {
    const widget = this.activeTrigger?.closest?.('[js-gift-product], [data-wrist-strap-widget]');
    const formId =
      widget?.dataset?.productFormId || widget?.querySelector?.('[form]')?.getAttribute('form');
    return formId ? document.getElementById(formId) : null;
  }

  submitProductForm(productForm) {
    const mainSubmitButton = productForm.closest('product-form')?.querySelector('[type="submit"]');
    if (mainSubmitButton?.getAttribute('aria-disabled') === 'true') {
      this.setErrorMessage('This product is currently unavailable.');
      return;
    }

    const productFormId = productForm.id || '';
    const matchesForm = (event) => event.detail?.productFormId === productFormId;
    const cleanup = () => {
      document.removeEventListener('product-form:submit-success', handleSuccess);
      document.removeEventListener('product-form:submit-error', handleError);
    };
    const handleSuccess = (event) => {
      setTimeout(() => {
        this.close();
      }, 500);

      if (!matchesForm(event)) return;
      cleanup();
      this.setCartSubmitLoading(false);
    };
    const handleError = (event) => {
      if (!matchesForm(event)) return;
      cleanup();
      this.setCartSubmitLoading(false);
      this.setErrorMessage(event.detail?.response?.description || 'Unable to add this product to the cart.');
    };

    document.addEventListener('product-form:submit-success', handleSuccess);
    document.addEventListener('product-form:submit-error', handleError);
    this.setCartSubmitLoading(true);

    if (typeof productForm.requestSubmit === 'function') {
      productForm.requestSubmit();
    } else {
      productForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  clearDirectCartActiveState() {
    const trigger = this.activeTrigger;
    const giftWidget = trigger?.closest?.('gift-widget');
    if (!giftWidget || !trigger?.classList.contains('is-remove')) return;

    giftWidget.handleBtn(trigger);
    giftWidget.handleFields(trigger);
    giftWidget.handlePrice();
  }

  setCartSubmitLoading(isLoading) {
    if (!this.cartAddButton) return;
    this.cartAddButton.classList.toggle('loading', isLoading);
    this.cartAddButton.disabled = isLoading || !this.selectedVariant?.available;
    this.cartAddButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
  }

  setErrorMessage(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
    }
  }

  updateGiftWidget(finalPriceCents) {
    const widgets = Array.from(document.querySelectorAll('[data-wrist-strap-widget]'));
    if (!widgets.length) return;

    widgets.forEach((widget) => this.applySelectionToWidget(widget, finalPriceCents));
  }

  getSelectedVariantImageSrc() {
    const activeGalleryImage = this.mediaGallery?.querySelector(
      '.product__media-item.is-active img, .product__media-item:not(.is-hidden) img',
    );
    if (activeGalleryImage?.currentSrc) return activeGalleryImage.currentSrc;
    if (activeGalleryImage?.src) return activeGalleryImage.src;

    return (
      this.selectedVariant?.featured_image?.src ||
      this.selectedVariant?.image?.src ||
      this.selectedVariant?.image ||
      this.selectedVariant?.featured_media?.preview_image?.src ||
      this.selectedVariant?.featured_media?.src ||
      ''
    );
  }

  applySelectionToWidget(widget, finalPriceCents) {
    const isStandalone = widget.dataset.standalone === 'true';
    const activeWidget = this.activeTrigger?.closest?.('[data-wrist-strap-widget]');
    const mirroredFromStandalone =
      !isStandalone && activeWidget?.dataset.standalone === 'true';
    const baseTitle = widget.dataset.baseTitle || '';
    const colorLabel = this.getDisplayColor() || this.selectedVariant.options[0];
    const titleEl = widget.querySelector('[data-wrist-strap-title]');
    const baseEl = widget.querySelector('[data-wrist-strap-base]');
    const colorEl = widget.querySelector('[data-wrist-strap-color]');
    const colorWrapper = widget.querySelector('[data-wrist-strap-color-wrapper]');
    const imageEl = widget.querySelector('.gift-widget__img img');
    const standaloneImageEl = widget.querySelector('.wrist-strap-block__image');
    const priceEl = widget.querySelector('[data-wrist-strap-price]');
    const chargeInput = widget.querySelector('[data-wrist-strap-charge-input]');
    const idInput = widget.querySelector('[data-wrist-strap-id-input]');
    const colorInput = widget.querySelector('[data-wrist-strap-color-input]');
    const triggerBtn =
      this.activeTrigger?.closest?.('[data-wrist-strap-widget]') === widget
        ? this.activeTrigger
        : widget.querySelector('[data-wrist-strap-trigger]');
    const removeBtn = widget.querySelector('[data-wrist-strap-remove]');

    const baseText = baseTitle || this.selectedVariant.title;
    if (baseEl) {
      baseEl.textContent = baseText;
    } else if (titleEl) {
      titleEl.textContent = `${baseText} - ${colorLabel}`;
    }

    if (colorEl) {
      colorEl.textContent = colorLabel;
    }

    if (colorWrapper) {
      colorWrapper.removeAttribute('hidden');
    }

    if (priceEl) {
      priceEl.innerHTML = this.getPriceHtml(finalPriceCents, this.selectedVariant.price, {
        wrapOriginal: !isStandalone,
      });
    }

    if (chargeInput) {
      chargeInput.value = (finalPriceCents / 100).toString();
    }

    if (idInput) {
      idInput.value = this.selectedVariant.id;
    }

    if (colorInput) {
      colorInput.value = colorLabel;
    }

    widget.dataset.selectedVariantId = this.selectedVariant.id;

    if (triggerBtn) {
      triggerBtn.setAttribute('data-price', (finalPriceCents / 100).toFixed(2));
      if (mirroredFromStandalone) {
        triggerBtn.dataset.syncedFromStandalone = 'true';
      } else {
        delete triggerBtn.dataset.syncedFromStandalone;
      }
    }

    const nextImageSrc = this.getSelectedVariantImageSrc() || widget.dataset.defaultImage;

    if (imageEl && nextImageSrc && isStandalone) {
      imageEl.src = nextImageSrc;
      imageEl.alt = baseText;
      imageEl.removeAttribute('srcset');
    }

    if (standaloneImageEl && nextImageSrc) {
      standaloneImageEl.src = nextImageSrc;
      standaloneImageEl.alt = baseText;
      standaloneImageEl.removeAttribute('srcset');
    }

    if (isStandalone) {
      widget.classList.add('is-selected');
      triggerBtn?.classList.add('is-selected');
      removeBtn?.removeAttribute('hidden');
      this.toggleStandaloneFields(widget, false);
      return;
    }

    const giftWidget = document.querySelector('gift-widget');
    if (giftWidget && typeof giftWidget.addGiftProduct === 'function' && triggerBtn) {
      giftWidget.addGiftProduct(triggerBtn);
    } else if (giftWidget && triggerBtn) {
      giftWidget.handleBtn(triggerBtn);
      giftWidget.handleFields(triggerBtn);
      giftWidget.handlePrice();
    }
  }

  resetWidget(widget) {
    const isStandalone = widget.dataset.standalone === 'true';
    const titleEl = widget.querySelector('[data-wrist-strap-base]');
    const colorWrapper = widget.querySelector('[data-wrist-strap-color-wrapper]');
    const colorEl = widget.querySelector('[data-wrist-strap-color]');
    const imageEl = widget.querySelector('.gift-widget__img img');
    const standaloneImageEl = widget.querySelector('.wrist-strap-block__image');
    const priceEl = widget.querySelector('[data-wrist-strap-price]');
    const chargeInput = widget.querySelector('[data-wrist-strap-charge-input]');
    const idInput = widget.querySelector('[data-wrist-strap-id-input]');
    const colorInput = widget.querySelector('[data-wrist-strap-color-input]');
    const triggerBtn = widget.querySelector('[data-wrist-strap-trigger]');
    const removeBtn = widget.querySelector('[data-wrist-strap-remove]');

    if (triggerBtn) {
      delete triggerBtn.dataset.syncedFromStandalone;
    }

    widget.classList.remove('is-selected');
    widget.dataset.selectedVariantId = '';

    if (titleEl && widget.dataset.baseTitle) {
      titleEl.textContent = widget.dataset.baseTitle;
    }

    colorWrapper?.setAttribute('hidden', 'hidden');

    if (colorEl) {
      colorEl.textContent = '';
    }

    const defaultImage = widget.dataset.defaultImage;
    const defaultImageAlt = widget.dataset.defaultImageAlt || widget.dataset.baseTitle || '';
    if (imageEl && defaultImage) {
      imageEl.src = defaultImage;
      imageEl.alt = defaultImageAlt;
      imageEl.removeAttribute('srcset');
    }

    if (standaloneImageEl && defaultImage) {
      standaloneImageEl.src = defaultImage;
      standaloneImageEl.alt = defaultImageAlt;
      standaloneImageEl.removeAttribute('srcset');
    }

    const defaultPrice = priceEl?.dataset.defaultPrice;
    if (priceEl && defaultPrice) {
      priceEl.innerHTML = defaultPrice;
    }

    if (chargeInput) {
      chargeInput.value = isStandalone ? '' : chargeInput.defaultValue;
    }

    if (idInput) {
      idInput.value = isStandalone ? '' : idInput.defaultValue;
    }

    if (colorInput) {
      colorInput.value = isStandalone ? '' : colorInput.defaultValue;
    }

    if (isStandalone) {
      triggerBtn?.classList.remove('is-selected');
      removeBtn?.setAttribute('hidden', 'hidden');
      this.toggleStandaloneFields(widget, true);
      return;
    }

    const giftWidget = document.querySelector('gift-widget');
    if (giftWidget && triggerBtn?.classList.contains('is-remove')) {
      if (typeof giftWidget.handleBtn === 'function') {
        giftWidget.handleBtn(triggerBtn);
      }
      if (typeof giftWidget.handleFields === 'function') {
        giftWidget.handleFields(triggerBtn);
      }
      if (typeof giftWidget.handlePrice === 'function') {
        giftWidget.handlePrice();
      }
    }
  }

  toggleStandaloneFields(widget, disabled) {
    const fields = widget.querySelectorAll('[js-wrist-strap-field]');
    const productFormId = widget.dataset.productFormId || '';
    fields.forEach((field) => {
      if (disabled) {
        field.setAttribute('disabled', 'disabled');
        field.removeAttribute('form');
      } else {
        if (productFormId) {
          field.setAttribute('form', productFormId);
        }
        field.removeAttribute('disabled');
      }
    });
  }

  addStrapToCart(finalPriceCents) {
    const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (!cartItems || !this.cartContext) return;

    const line = Number(this.cartContext.line) || 1;
    const quantity = Number(this.cartContext.quantity) || 1;
    const charges = (finalPriceCents / 100).toString();
    const surrogateTarget = {
      dataset: {
        isDiscount: this.cartContext.isDiscount || 'false',
        discount: this.cartContext.discount || '0',
      },
    };

    cartItems.updateComponent(
      cartItems,
      line,
      quantity,
      'wrist',
      this.selectedVariant.id,
      charges,
      surrogateTarget,
    );

    this.close();
    this.openCartDrawer();
  }

  updateCtaLabel() {
    if (this.ctaText) {
      this.ctaText.textContent = this.cartContext ? 'Add to bag' : 'Add to product';
    }
    if (this.cartAddButton) {
      const hideCartAddButton = Boolean(this.cartContext);
      this.cartAddButton.hidden = hideCartAddButton;
      this.cartAddButton.style.display = hideCartAddButton ? 'none' : '';
      this.cartAddButton.disabled = hideCartAddButton || !this.selectedVariant?.available;
      if (hideCartAddButton) {
        this.cartAddButton.setAttribute('aria-hidden', 'true');
        this.cartAddButton.setAttribute('tabindex', '-1');
      } else {
        this.cartAddButton.removeAttribute('aria-hidden');
        this.cartAddButton.removeAttribute('tabindex');
      }
    }
  }

  closeCartDrawer() {
    if (this.cartContext && this.cartDrawer) {
      this.cartDrawer.close(true);
    }
  }

  openCartDrawer() {
    if (this.cartDrawer) {
      this.cartDrawer.open();
    }
  }
}

customElements.define('wrist-strap-drawer', WristStrapDrawer);
